import React from 'react';
import Logo from '../components/Logo';
import { IconTarget, IconGlobe, IconEye, IconTrend, IconCode } from '../components/Icons';
import './About.css';

const VALUES = [
  { Icon: IconTarget, title: 'Developer-First',  desc: 'Every product decision starts with the developer. We build tools that real developers need in real workflows.' },
  { Icon: IconGlobe,  title: 'Community Focus',  desc: 'Discord communities are where developers collaborate. We meet them there, in the tools they already use.' },
  { Icon: IconEye,    title: 'Transparency',     desc: 'Open about how our bots work, what data they use, and how we keep it secure.' },
  { Icon: IconTrend,  title: 'Constant Growth',  desc: 'We ship updates fast, take feedback seriously, and always push for a better developer experience.' },
];

function CheckItem({ children }) {
  return (
    <li className="about-check-item">
      <svg className="about-check-icon" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
        <circle cx="9" cy="9" r="9" fill="var(--blue-light)"/>
        <path d="M5.5 9.5l2.5 2.5 4.5-5" stroke="var(--blue-dark)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      {children}
    </li>
  );
}

export default function About() {
  return (
    <div className="page about-page">

      <section className="about-hero">
        <div className="about-hero__blob" aria-hidden="true" />
        <div className="container animate-fade-up">
          <span className="eyebrow">About PERM Systems</span>
          <h1 className="display-title" style={{ maxWidth: 740 }}>
            Making the developer<br />
            experience on Discord<br />
            <span style={{ color: 'var(--blue-dark)' }}>better.</span>
          </h1>
          <p className="section-body">
            PERM Systems is a solo-operated organisation dedicated to building tools
            that improve how developers work, learn, and collaborate on Discord.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container about-story">
          <div className="about-story__text animate-fade-up">
            <span className="eyebrow">The Story</span>
            <h2 className="section-title">One developer.<br />One clear mission.</h2>
            <div className="rule" />
            <p>
              PERM Systems was founded in 2026 by a single developer who noticed a
              consistent gap: Discord is where developers spend a significant amount
              of time, yet the tooling available to support their actual work was
              almost nonexistent.
            </p>
            <p>
              Code discussions lacked real assistance. Debugging required constant
              context-switching out of Discord. There was no native way to get
              intelligent coding help inside the platform where the conversation
              was already happening.
            </p>
            <p>
              PERM Systems set out to change that. Starting with Code Assist —
              a fully custom AI model built and managed by PERM Systems — the
              goal is to systematically remove friction from the developer workflow
              on Discord, one bot at a time.
            </p>
            <p className="about-story__note">
              PERM Systems is currently a one-person operation. Every bot, every line
              of code, and every support response comes directly from the founder.
            </p>
          </div>
          <div className="about-story__visual animate-fade-up delay-2">
            <div className="about-story__card">
              <Logo size={48} />
              <div className="about-story__card-info">
                <h3>PERM Systems</h3>
                <p>Developer experience on Discord</p>
                <div className="about-story__card-meta">
                  <span>Founded 2026</span>
                  <span className="about-story__dot" />
                  <span>Solo-operated</span>
                </div>
              </div>
            </div>
            <div className="about-story__focus">
              <p className="about-story__focus-label">Focus areas</p>
              <ul>
                <CheckItem>AI-powered coding assistance</CheckItem>
                <CheckItem>Developer workflow automation</CheckItem>
                <CheckItem>Discord-native tooling</CheckItem>
                <CheckItem>Open to community suggestions</CheckItem>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="section about-mv">
        <div className="container">
          <div className="about-mv__grid">
            <div className="about-mv__block animate-fade-up delay-1">
              <span className="eyebrow">Mission</span>
              <h3>Why PERM Systems exists</h3>
              <p>
                To improve the developer experience on Discord by building intelligent,
                reliable tools that integrate seamlessly into the platform developers
                already use — removing context-switching, reducing friction, and making
                technical collaboration faster.
              </p>
            </div>
            <div className="about-mv__block animate-fade-up delay-2">
              <span className="eyebrow">Vision</span>
              <h3>Where this is headed</h3>
              <p>
                A future where every developer Discord server — from a five-person
                study group to a 100,000-member open source community — has access to
                the same quality of AI tooling that enterprise teams pay thousands for.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section about-note-section">
        <div className="container">
          <div className="about-note animate-fade-up">
            <div className="about-note__icon">
              <IconCode size={22} color="var(--blue-dark)" />
            </div>
            <div>
              <h3>We do not build custom bots on request</h3>
              <p>
                PERM Systems does not offer custom bot development for individual servers
                or organisations. However, we actively welcome recommendations for new
                bots or features that could improve the developer experience more broadly.
                If you have an idea, <a href="/contact">share it with us</a> — it may
                become the next PERM Systems product.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section" style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <div className="animate-fade-up mob-center">
            <span className="eyebrow">Core Principles</span>
            <h2 className="section-title">What drives us</h2>
          </div>
          <div className="grid-4" style={{ marginTop: 52 }}>
            {VALUES.map(({ Icon, title, desc }, i) => (
              <div key={title} className={`about-value animate-fade-up delay-${i + 1}`}>
                <div className="icon-wrap"><Icon size={20} /></div>
                <h4>{title}</h4>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ── Free + Giving section ── */}
      <section className="section about-giving">
        <div className="container">
          <div className="about-giving__inner animate-fade-up">
            <div className="about-giving__left">
              <span className="eyebrow">Our Commitment</span>
              <h2 className="section-title">Free to use.<br/>Giving back.</h2>
              <div className="rule"/>
              <p className="section-body">
                Every PERM Systems product is completely free. No subscriptions,
                no paywalls, no feature-locked tiers. Developer tooling should be
                accessible to everyone — a first-year student and a senior engineer
                should have the same tools available to them.
              </p>
              <p className="section-body" style={{marginTop: 12}}>
                When users choose to donate to support PERM Systems, 100% of those
                donations are passed directly to{' '}
                <a href="https://thewaterproject.org" target="_blank" rel="noopener noreferrer"
                   className="about-giving__link">
                  The Water Project
                </a>
                {' '}— a non-profit organisation that builds sustainable water projects
                across sub-Saharan Africa.
              </p>
              <p className="section-body" style={{marginTop: 12}}>
                PERM Systems keeps nothing from donations. The project sustains itself
                through community growth and future plans — not by monetising its users.
              </p>
            </div>
            <div className="about-giving__cards animate-fade-up delay-2">
              <div className="about-giving__card about-giving__card--free">
                <div className="about-giving__card-icon">
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
                    <circle cx="11" cy="11" r="11" fill="rgba(173,216,255,0.2)"/>
                    <polyline points="5.5 11 9 14.5 16.5 7" stroke="var(--blue-dark)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h4>Always Free</h4>
                <p>No subscriptions. No paywalls. Every product is free for every user, forever.</p>
              </div>
              <div className="about-giving__card about-giving__card--water">
                <div className="about-giving__card-icon">
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
                    <path d="M11 19s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11z" fill="rgba(96,165,250,0.15)" stroke="#60a5fa" strokeWidth="1.8"/>
                  </svg>
                </div>
                <h4>Donations to The Water Project</h4>
                <p>
                  100% of all donations are passed to{' '}
                  <a href="https://thewaterproject.org" target="_blank" rel="noopener noreferrer"
                     className="about-giving__link">
                    The Water Project
                  </a>
                  . PERM Systems keeps nothing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}